import { Badge } from '@/components/ui/badge';
import { GraduationCap, ExternalLink } from 'lucide-react';
import { useOptiBayTrainingRecommendations } from '@/hooks/useOptiBayTrainingRecommendations';
import type { OptiBaySession } from '@/types/optibay';

interface OptiBayTrainingHintsProps {
  session: OptiBaySession;
  className?: string;
}

/**
 * Lightweight training hint panel shown contextually during OptiBay stages.
 * Displays relevant training recommendations based on the selected system family.
 * Metadata-only: shows title, provider, and link. No content scraping.
 */
export function OptiBayTrainingHints({ session, className }: OptiBayTrainingHintsProps) {
  const { data: recommendations = [], isLoading } = useOptiBayTrainingRecommendations(session);

  if (isLoading || recommendations.length === 0) return null;

  return (
    <div className={className}>
      <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
        <div className="flex items-center gap-1.5">
          <GraduationCap className="h-3.5 w-3.5 text-primary" />
          <span className="text-xs font-semibold">Recommended Training</span>
          <Badge variant="secondary" className="text-[10px]">{recommendations.length}</Badge>
        </div>
        <div className="space-y-1.5">
          {recommendations.slice(0, 3).map(rec => (
            <div key={rec.id} className="text-xs space-y-0.5">
              <p className="font-medium">{rec.title}</p>
              {rec.description && (
                <p className="text-muted-foreground text-[10px] line-clamp-2">{rec.description}</p>
              )}
              {rec.matchingArtifacts && rec.matchingArtifacts.length > 0 && (
                <div className="flex gap-1 flex-wrap mt-1">
                  {rec.matchingArtifacts.map(a => (
                    <Badge key={a.id} variant="outline" className="text-[9px] gap-0.5">
                      <ExternalLink className="h-2 w-2" />
                      {a.title.slice(0, 30)}{a.title.length > 30 ? '…' : ''}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
